import React, {createContext, useContext, useState, useRef, useMemo, useEffect} from 'react'
import {useStickyState} from './sticky-context'

const HeaderContext = createContext()

export const HeaderProvider = ({children}) => {
    const lastScrollY = useRef(0)
    const [shouldShowSticky, setShouldShowSticky] = useState(false)
    const [headerHeight, setHeaderHeight] = useState(0)
    const headerRef = useRef(null)
    const [scrollDir, setScrollDir] = useState('up')
    const [scrollChange, setScrollChange] = useState(0)
    const {pdpStickyButtonActive} = useStickyState()
    const [shouldShowHeader, setShouldShowHeader] = useState(true)
    const [category, setCategory] = useState(null)
    const [product, setProduct] = useState(null)
    const [pageType, setPageType] = useState(null)

    // Header variant state: '1' (transparent white), '2' (transparent black), '3' (solid white)
    const [headerVariant, setHeaderVariant] = useState('3')
    // Gradient and drop shadow config (set by HeaderVariantSetter from env vars)
    const [headerGradient, setHeaderGradient] = useState('none')
    const [headerDropShadow, setHeaderDropShadow] = useState(false)
    // Scroll state shared across header and related components
    const [isScrolled, setIsScrolled] = useState(false)
    // Hover zone for transparent header: 'none' | 'nav' | 'icons' | 'gap'
    const [headerHoverZone, setHeaderHoverZone] = useState('none')

    useEffect(() => {
        const handleScroll = () => {
            const currentY = window.scrollY

            // Always update isScrolled so transparent header restores at top
            setIsScrolled(currentY > 0)

            if (Math.abs(currentY - lastScrollY.current) < 5) return

            if (currentY > lastScrollY.current) {
                setScrollDir('down')
            } else {
                setScrollDir('up')
            }

            lastScrollY.current = currentY

            setScrollChange(currentY)
        }

        window.addEventListener('scroll', handleScroll)
        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    useEffect(() => {
        setShouldShowHeader(scrollDir === 'up' || !pdpStickyButtonActive)
    }, [scrollChange])

    const contextValue = useMemo(
        () => ({
            headerRef,
            shouldShowSticky,
            setShouldShowSticky,
            headerHeight,
            setHeaderHeight,
            shouldShowHeader,
            setShouldShowHeader,
            category,
            setCategory,
            product,
            setProduct,
            pageType,
            setPageType,
            headerVariant,
            setHeaderVariant,
            headerGradient,
            setHeaderGradient,
            headerDropShadow,
            setHeaderDropShadow,
            isScrolled,
            headerHoverZone,
            setHeaderHoverZone
        }),
        [
            shouldShowSticky,
            headerHeight,
            shouldShowHeader,
            category,
            product,
            pageType,
            headerVariant,
            headerGradient,
            headerDropShadow,
            isScrolled,
            headerHoverZone
        ]
    )

    return <HeaderContext.Provider value={contextValue}>{children}</HeaderContext.Provider>
}

export const useHeaderContext = () => {
    const context = useContext(HeaderContext)
    if (!context) {
        throw new Error('useHeaderContext must be used within HeaderProvider')
    }
    return context
}
